import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Commons from './controls/Commons';
import { clearListAllAdministrativeServicesData } from "src/redux/administrativeService/administrativeServiceActions";
import ExpedienteContainer from './controls/ExpedienteContainer';
import { clearNewAffectationAdministrativeDocumentData } from 'src/redux/affectation/affectationActions';
import { clearListOrderPayLast } from 'src/redux/orderPay/orderPayActions';
import { NumericFormat } from 'react-number-format';
import FormFieldError from 'src/components/general/FormFieldError';
import TotalAmountReceiptValidationForm from './TotalAmountReceiptValidationForm';
import { getAccessToken } from 'src/redux/login/loginReducer';
import { getProvisionOrderListIsFetching } from 'src/redux/orderPay/orderPayReducer';
import { getGlobalDataSelectedPeriod } from 'src/redux/globalData/globalDataReducer';
import { getListIdsServiceAdministrativeByUser } from 'src/redux/administrativeService/administrativeServiceReducer';
import PayrollTableWithPagination from 'src/components/pages/Payroll/PayrollTableWithPagination';
import { payrollService } from 'src/services/payrollService';
import PayrollTable from 'src/components/pages/Payroll/PayrollTable';

const NewHaberesLiquidationForm = () => {
	const dispatch = useDispatch();
	const { fundRequest, orderPay, expedientesAsociados,
		setExpedientesAsociados, administrativeServiceData, settlementOfAssets,
		importe, expedientesSeleccionados, setAllowSaveAmountValidation,
		register, errors, setValue
	} = useFormContext();
	const [administrativeDocument, setAdministrativeDocument] = useState([]);
	const [admServiceId, setAdmServiceId] = useState('');
	const applyFilterBankAccounts = (administrativeServiceId) => {
		setAdmServiceId(administrativeServiceId);
	};
	const selectedExpedientAssociated = expedientesAsociados?.filter(i => i.selected);
	const liquidationsValidationObj = { required: 'Debe cargar una Liquidación.' };
	const [payrollData, setPayrollData] = useState(null);
	const selectedPeriod = useSelector(state => getGlobalDataSelectedPeriod(state));
	const listIdsServiceAdministrative = useSelector(state => getListIdsServiceAdministrativeByUser(state));
	const isFetching = useSelector(state => getProvisionOrderListIsFetching(state));
	const accessToken = useSelector(state => getAccessToken(state));
	const [isFetchingPayrollList, setIsFetchingPayrollList] = useState(false);
	const shouldFetchOnFilterChange = useRef(false);
	const [filters, setFilters] = useState({
		id: '',
		wageLiquidationType: '',
		name: '',
		description: '',
		netAmount: '',
		generalAmount: '',
	});

	const handleFilterChange = (filterChanges) => {
		setFilters(prev => ({
			...prev,
			...filterChanges
		}));
	};

	const clearFilters = () => {
		setFilters({
			id: '',
			wageLiquidationType: '',
			name: '',
			description: '',
			netAmount: '',
			generalAmount: '',
		});
		shouldFetchOnFilterChange.current = true;
	};

	const handleApplyFilters = () => {
		shouldFetchOnFilterChange.current = true;
	};

	// Modificar la función fetchPayrollList para usar los filtros
	const fetchPayrollList = async (pageNumber = 0, serviceId) => {
		try {
			setIsFetchingPayrollList(true);
			const params = {
				page: pageNumber,
				size: 10,
				sort: 'id,desc',
				administrativeServiceId: serviceId,
				year: selectedPeriod?.year,
				state: "BORRADOR",
				...(filters.id && { id: filters.id }),
				...(filters.wageLiquidationType && { wageLiquidationType: filters.wageLiquidationType }),
				...(filters.name && { name: filters.name }),
				...(filters.description && { description: filters.description }),
				...(filters.netAmount && { netAmount: filters.netAmount }),
				...(filters.generalAmount && { generalAmount: filters.generalAmount }),
			};
			const data = await payrollService.getPayrollList(accessToken, params);
			setPayrollData(data);
		} catch (error) {
			console.error("Error:", error);
		} finally {
			setIsFetchingPayrollList(false);
		}
	};

	const onPageChange = (pNumber) => {
		fetchPayrollList(pNumber - 1, admServiceId);
	};

	const [selectedPayroll, setSelectedPayroll] = useState([]);
	const [associatedPayroll, setAssociatedPayroll] = useState([]);
	const [checkedAssociatedPayroll, setCheckedAssociatedPayroll] = useState([]);

	const handleSelectPayroll = (payroll) => {
		const isAlreadySelected = selectedPayroll.some(selected => selected.id === payroll.id);
		if (isAlreadySelected) {
			setSelectedPayroll(prev => prev.filter(item => item.id !== payroll.id));
		} else {
			setSelectedPayroll(prev => [...prev, payroll]);
		}
	};

	const handleAssociatePayroll = (payroll) => {
		const isAlreadyChecked = checkedAssociatedPayroll.some(checked => checked.id === payroll.id);
		if (isAlreadyChecked) {
			setCheckedAssociatedPayroll(prev => prev.filter(item => item.id !== payroll.id));
		} else {
			setCheckedAssociatedPayroll(prev => [...prev, payroll]);
		}
	}

	const selectedPayrollIds = useMemo(() => {
		const selectedIds = selectedPayroll.map(item => item.id);
		const associatedIds = checkedAssociatedPayroll.map(item => item.id);
		return [...new Set([...selectedIds, ...associatedIds])];
	}, [selectedPayroll, checkedAssociatedPayroll]);

	// Calcular el monto total de las liquidaciones seleccionadas y asociadas 
	const totalSelectedAmount = useMemo(() => {
		const selectedAmount = selectedPayroll.reduce((sum, item) => sum + (Number(item.generalAmount) || 0), 0);
		const associatedAmount = checkedAssociatedPayroll.reduce((sum, item) => sum + (Number(item.generalAmount) || 0), 0);
		return selectedAmount + associatedAmount;
	}, [selectedPayroll, checkedAssociatedPayroll]);

	// Actualizar los IDs y el monto total en el contexto del formulario cuando cambie la selección
	useEffect(() => {
		register('selectedPayrollIds');
		setValue('selectedPayrollIds', selectedPayrollIds);
		register('selectedPayrollTotalAmount');
		setValue('selectedPayrollTotalAmount', totalSelectedAmount);
	}, [selectedPayrollIds, totalSelectedAmount, setValue, register]);

	useEffect(() => {
		if (shouldFetchOnFilterChange.current && admServiceId) {
			fetchPayrollList(0, admServiceId);
			shouldFetchOnFilterChange.current = false;
		}
	}, [filters]);

	useEffect(() => {
		if (orderPay) {
			setAdmServiceId(orderPay.administrativeServiceId);
			fetchPayrollList(0, orderPay.administrativeServiceId);
		} else {
			fetchPayrollList(0, admServiceId);
		}
	}, [accessToken, listIdsServiceAdministrative, selectedPeriod?.year, admServiceId]);

	useEffect(() => {
		if (orderPay) {
			setAdmServiceId(orderPay.administrativeServiceId);
			fetchPayrollList(0, orderPay.administrativeServiceId);
		}
		if (fundRequest) {
			setAdmServiceId(fundRequest.administrativeServiceId);
		}
		dispatch(clearListOrderPayLast());
		dispatch(clearListAllAdministrativeServicesData());
		dispatch(clearNewAffectationAdministrativeDocumentData());
	}, []);

	useEffect(() => {
		if (orderPay && orderPay.wageManagers && orderPay.wageManagers.length > 0) {
			const formattedWageManagers = orderPay.wageManagers.map(wageManager => ({
				id: wageManager.id,
				generalAmount: wageManager.generalAmount,
				...wageManager
			}));
			setAssociatedPayroll(formattedWageManagers);
			setCheckedAssociatedPayroll(formattedWageManagers);
		}
	}, [orderPay]);

	return <>
		<Commons
			administrativeServiceData={administrativeServiceData}
			admServiceId={admServiceId}
			setAdmServiceId={setAdmServiceId}
			applyFilterBankAccounts={applyFilterBankAccounts}
			disabledImporte={true}
			disableAdmExpedientSearch={true}
			obligatoryLegalInstrument={true}
		/>
		{admServiceId &&
			<>
				<h6 className='mt-4'>Listado de Liquidaciones de Haberes</h6>
				<PayrollTableWithPagination
					payrollData={payrollData}
					isFetching={isFetching || isFetchingPayrollList}
					mode="SELECT"
					onPageChange={onPageChange}
					onSelect={handleSelectPayroll}
					selectedItems={selectedPayroll}
					associatedItems={checkedAssociatedPayroll}
					filters={filters}
					onFilterChange={handleFilterChange}
					onClearFilters={clearFilters}
					onApplyFilters={handleApplyFilters}
				/>
				{selectedPayroll.length > 0 && (
					<>
						<h6 className='mt-4'>Liquidaciones de Haberes seleccionadas</h6>
						<PayrollTable
							records={selectedPayroll}
							isFetching={isFetching}
							mode="SELECT"
							onSelect={handleSelectPayroll}
							selectedItems={selectedPayroll}
						/>
					</>)
				}
				{associatedPayroll.length > 0 && (
					<>
						<h6 className='mt-4'>Liquidaciones de Haberes asociadas</h6>
						<PayrollTable
							records={associatedPayroll}
							isFetching={isFetching}
							mode="ASSOCIATE"
							onAssociate={handleAssociatePayroll}
							associatedItems={checkedAssociatedPayroll}
						/>
					</>)
				}
				{checkedAssociatedPayroll.length === 0 && selectedPayroll.length === 0 &&
					<Alert variant='warning' className='mt-4 mb-5'>
						<FontAwesomeIcon icon={faExclamationTriangle} className='text-black-color mr-3' />
						Debe Seleccionar una Liquidación de Haberes.
					</Alert>
				}
				{/* Campos ocultos "hidden" para mantener los datos en el contexto del formulario */}
				<Form.Control
					type="hidden"
					name="selectedPayrollIds"
					value={JSON.stringify(selectedPayrollIds)}
				/>
				<Form.Control
					type="hidden"
					name="selectedPayrollTotalAmount"
					value={totalSelectedAmount}
				/>
			</>
		}
		{settlementOfAssets?.generalAmount &&
			<>
				<Form.Group as={Row} className='mt-3' >
					<Form.Label htmlFor='asunto' className='text-right d-flex mandatory-label' column sm='4'>
						Método de contratación:
					</Form.Label>
					<Col sm='4'>
						<Form.Control
							type='text'
							name='recruitmentMethod'
							value={settlementOfAssets?.wageLiquidationType?.replace(/_/g, ' ')}
							disabled
						/>
					</Col>
				</Form.Group>
				<Form.Group as={Row}>
					<Form.Label htmlFor='asunto' className='text-right d-flex mandatory-label' column sm='4'>
						Total:
					</Form.Label>
					<Col sm='4'>
						<NumericFormat
							prefix={'$'}
							thousandSeparator={'.'}
							decimalSeparator={','}
							decimalScale={2}
							value={settlementOfAssets?.generalAmount}
							className={'form-control text-left'}
							disabled
						/>

					</Col>
				</Form.Group>
			</>
		}
		<Form.Group as={Row}>
			<Form.Label htmlFor='asunto' className='text-right d-flex mandatory-label' column sm='4'>
			</Form.Label>
			<Col sm='4'>
				<Form.Control
					value={settlementOfAssets?.generalAmount ? 'true' : ''}
					type='hidden'
					name='liquidationListValidation'
				/>
			</Col>
		</Form.Group>
		{admServiceId &&
			<ExpedienteContainer
				expedientesAsociados={expedientesAsociados}
				setExpedientesAsociados={setExpedientesAsociados}
				administrativeDocument={administrativeDocument}
				setAdministrativeDocument={setAdministrativeDocument}
				disableAdmExpedientSearch={true}
				showSubpartItemSelector={true}
			/>
		}
		<TotalAmountReceiptValidationForm
			setAllowSaveAmountValidation={setAllowSaveAmountValidation}
			expedientesSeleccionados={expedientesSeleccionados}
			selectedExpedientAssociated={selectedExpedientAssociated}
			totalAmountReceipts={totalSelectedAmount}
			importe={importe}
			// receipts={settlementOfAssets?.wageManagerDetails}
			receipts={{}}
		/>
	</>
};

export default NewHaberesLiquidationForm;